import { facilityAcronym } from "@app/shared/utils/facility-acronym";

export interface GlobalInmateSearchResponse {
  query: string;
  totalCount: number;
  limit: number;
  results: GlobalInmateSearchResult[];
}

export interface GlobalInmateSearchResult {
  incarceratedPersonId: number;
  inmateNumber: string;
  displayName: string;
  matchSummary: string;
  dateOfBirth?: string | null;
  facilityCode?: string | null;
  facilityLabel?: string | null;
  offenseCategoryCode?: string | null;
  offenseCategoryLabel?: string | null;
  hearingStatusCode?: string | null;
  hearingStatusLabel?: string | null;
  eligibilityDate?: string | null;
  hearingDate?: string | null;
  detailUrl: string;
}

export interface GlobalSearchTrayRow extends GlobalInmateSearchResult {
  summary: string;
}

export interface GlobalSearchTrayPayload {
  results: GlobalSearchTrayRow[];
  model: GlobalSearchTrayColumn[];
  totalCount: number;
  recordCount: number;
}

export interface GlobalSearchTrayColumn {
  name: string;
  displayName: string;
  type: "string" | "date" | "dateTime" | "httpaction" | "link";
  thAlign?: string;
  tdAlign?: string;
  wrapText?: string;
  options?: GlobalSearchTrayColumnOptions;
}

/**
 * Per-column render options consumed by the masthead search tray. The `httpaction` keys
 * (buttonText/clickHandler/isHidden/textForHiddenAction) mirror what masthead-search.component.html
 * already reads at runtime; they are declared here so callers building columns get type help.
 */
export interface GlobalSearchTrayColumnOptions {
  text?: string;
  routerLink?: (row: GlobalSearchTrayRow) => unknown[];
  getValue?: (row: GlobalSearchTrayRow) => string;
  buttonText?: string | ((row: GlobalSearchTrayRow, roles?: unknown) => string);
  isHidden?: (row: GlobalSearchTrayRow) => boolean;
  textForHiddenAction?: (row: GlobalSearchTrayRow) => string;
  clickHandler?: (row: GlobalSearchTrayRow, ...args: unknown[]) => void;
  service?: unknown;
  [key: string]: unknown;
}

/**
 * Strategy a feature page can register with GlobalSearchService to override how the top-nav inmate
 * search renders its results — without changing the default behavior on every other page.
 */
export interface GlobalSearchContext {
  buildPayload(response: GlobalInmateSearchResponse): GlobalSearchTrayPayload;
}

/** Tray "Summary" cell: facility acronym + hearing status, e.g. "CEN | Entered". */
export function buildInmateSummary(row: {
  facilityLabel?: string | null;
  facilityCode?: string | null;
  hearingStatusLabel?: string | null;
  hearingStatusCode?: string | null;
}): string {
  const facility = facilityAcronym(row.facilityCode, row.facilityLabel);
  const status = row.hearingStatusLabel || row.hearingStatusCode;

  return [facility === "-" ? null : facility, status].filter(Boolean).join(" | ") || "No summary available";
}
